<template>
  <TooltipProvider :delay-duration="200">
    <div
      v-if="callActive"
      v-show="!callHidden"
      class="call-container"
      :class="{ minimized: callMinimized }"
    >
      <div class="call-header">
        <span class="call-title">{{ $t("call.videoCallTitle") }}</span>
        <div class="call-header-actions">
          <Tooltip>
            <TooltipTrigger as-child>
              <button
                type="button"
                class="call-header-btn call-header-btn--minmax"
                :aria-label="
                  callMinimized
                    ? $t('call.expand', 'Expand')
                    : $t('call.minimize', 'Minimize')
                "
                @click="toggleMinimized"
              >
                <Maximize2 v-if="callMinimized" class="call-header-icon" />
                <Minimize2 v-else class="call-header-icon" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="z-[2100]">
              {{
                callMinimized
                  ? $t("call.expand", "Expand")
                  : $t("call.minimize", "Minimize")
              }}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger as-child>
              <button
                type="button"
                class="call-header-btn"
                :aria-label="$t('call.hide', 'Hide')"
                @click="callHidden = true"
              >
                <EyeOff class="call-header-icon" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="z-[2100]">
              {{ $t("call.hide", "Hide") }}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger as-child>
              <button
                type="button"
                class="call-header-btn call-header-btn--close"
                :aria-label="$t('call.close', 'Close')"
                @click="closeCall"
              >
                <X class="call-header-icon" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="z-[2100]">
              {{ $t("call.close", "Close") }}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <CallForm v-if="callPhase === 'form'" />
      <iframe
        v-else
        :src="callIframeSrc"
        class="call-iframe"
        allow="camera; microphone; display-capture; autoplay; geolocation"
      ></iframe>
    </div>
    <button
      v-if="callActive && callHidden"
      type="button"
      class="call-fab"
      :aria-label="$t('call.resume', 'Resume call')"
      @click="callHidden = false"
    >
      <Video class="call-fab-icon" />
    </button>
  </TooltipProvider>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Maximize2, Minimize2, EyeOff, Video, X } from "lucide-vue-next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import { useCallWindow } from "../model/useCallWindow";
import CallForm from "./CallForm.vue";

const CALL_BASE_URL = "https://inqbox.q19.kz/wc";
const CALL_HOST_NAME = "https://inqbox.q19.kz";

const store = useStore();
const { locale } = useI18n();
const {
  callActive,
  callMinimized,
  callHidden,
  callTargetOperatorId,
  callQueueType,
  callRequestId,
  callPhase,
  closeCall,
} = useCallWindow();

const CALL_DOMAIN = "e-notary.q19.kz";

function toggleMinimized() {
  callMinimized.value = !callMinimized.value;
}

const callIframeSrc = computed(() => {
  if (!callActive.value) return "";
  const params = new URLSearchParams();
  params.set("call_media", "video");
  params.set("host_name", CALL_HOST_NAME);
  params.set("lang", locale.value || "ru");
  const userId = store.getters.get_self_user?.uuid;
  if (userId) {
    params.set("user_id", userId);
  }
  const theme = document.documentElement.classList.contains("dark")
    ? "dark"
    : "light";
  params.set("theme", theme);
  if (callTargetOperatorId.value) {
    params.set("target_operator_id", callTargetOperatorId.value);
  }
  if (callQueueType.value) {
    params.set("topic", `${callQueueType.value}_ru`);
  }
  if (callRequestId.value) {
    params.set("request_id", callRequestId.value);
  }
  params.set("domain", CALL_DOMAIN);
  return `${CALL_BASE_URL}/?${params.toString()}`;
});
</script>

<style lang="scss" scoped>
.call-container {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  background: hsl(var(--background));
  border-radius: 0;
  box-shadow: none;
  border: none;
  overflow: hidden;
  transition: width 0.3s ease, height 0.3s ease, bottom 0.3s ease,
    right 0.3s ease, border-radius 0.3s ease, box-shadow 0.3s ease;

  &.minimized {
    @apply rounded-lg;
    bottom: 20px;
    right: 20px;
    width: 370px;
    height: 600px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    border: 1px solid hsl(var(--border));

    .call-header {
      @apply px-4 py-3;
    }

    .call-title {
      @apply font-semibold;
      font-size: 0.875rem;
    }

    .call-header-btn {
      width: 24px;
      height: 24px;
    }
  }

  .call-header {
    @apply flex items-center justify-between flex-shrink-0;
    padding: 10px 16px;
    border-bottom: 1px solid hsl(var(--border));
    background: hsl(var(--sidebar-background));
  }

  .call-title {
    @apply font-semibold;
    font-size: 0.85rem;
    color: hsl(var(--foreground));
  }

  .call-header-actions {
    @apply flex items-center gap-1;
  }

  .call-header-btn {
    @apply w-8 h-8 flex items-center justify-center rounded-full cursor-pointer border-0 bg-transparent p-0;
    color: hsl(var(--muted-foreground));

    &:hover {
      background: hsl(var(--accent));
      color: hsl(var(--foreground));
    }

    &--close:hover {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
  }

  .call-header-icon {
    width: 16px;
    height: 16px;
  }

  .call-iframe {
    width: 100%;
    flex: 1;
    border: none;
  }

  @media (max-width: 900px) {
    // On mobile the call window is always fullscreen — neutralize
    // the corner-window styling that .minimized would otherwise apply.
    &.minimized {
      @apply rounded-none;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      box-shadow: none;
      border: none;

      .call-header {
        @apply px-4;
        padding-top: 10px;
        padding-bottom: 10px;
      }

      .call-title {
        font-size: 0.85rem;
      }

      .call-header-btn {
        width: 32px;
        height: 32px;
      }
    }

    .call-header-btn--minmax {
      display: none;
    }
  }
}

.call-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2000;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  animation: call-fab-pulse 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
}

.call-fab-icon {
  width: 24px;
  height: 24px;
}

@keyframes call-fab-pulse {
  0%,
  100% {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25),
      0 0 0 0 hsl(var(--primary) / 0.4);
  }
  50% {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25),
      0 0 0 12px hsl(var(--primary) / 0);
  }
}

@media (max-width: 900px) {
  .call-fab {
    bottom: 16px;
    right: 16px;
  }
}
</style>
